import { createSlice } from "@reduxjs/toolkit";
import { bloqcibeApi } from "./apiSlice";
import jwt_decode from "jwt-decode";

const SYSTEM_ADMIN_ROLE = "system_admin";

/**
 * System admins are provisioned in Keycloak (JWT `details`), not in getMe userRoles.
 */
export const getTokenRoles = (identity) => {
  if (!identity?.details || !Array.isArray(identity.details)) {
    return [];
  }
  const firstLayer = identity.details[0];
  if (!firstLayer) {
    return [];
  }
  const entries = Array.isArray(firstLayer) ? firstLayer : [firstLayer];
  return entries.flatMap((entry) => {
    if (Array.isArray(entry)) {
      return entry.filter((x) => x && typeof x === "object" && "role" in x);
    }
    if (entry && typeof entry === "object" && "role" in entry) {
      return [entry];
    }
    return [];
  });
};

export const isSystemAdminFromToken = (identity) =>
  getTokenRoles(identity).some((r) => r.role === SYSTEM_ADMIN_ROLE);

let savedToken = localStorage.getItem("token");
if (savedToken) {
  savedToken = JSON.parse(savedToken);
}

let persistedUserData = null;
try {
  const savedUserData = localStorage.getItem("userData");
  if (savedUserData) {
    persistedUserData = JSON.parse(savedUserData);
  }
} catch (error) {
  console.error("Error loading persisted user data:", error);
}

const tokenUser = savedToken ? jwt_decode(savedToken.access_token) : null;
const initialIsSystemAdmin = isSystemAdminFromToken(tokenUser);

const deriveFlagsFromUserData = (userData) => {
  const roles = userData?.userRoles || [];
  return {
    subjectLoggedIn: roles.some((r) => r.role === "subject"),
    sponsorId: userData?.sponsorId ?? null,
  };
};

const persistedFlags = persistedUserData
  ? deriveFlagsFromUserData(persistedUserData)
  : {
      subjectLoggedIn: false,
      sponsorId: null,
    };

const applyTokenToState = (state, payload) => {
  state.token = payload;
  state.user = jwt_decode(payload.access_token);
  state.isSystemAdmin = isSystemAdminFromToken(state.user);
  localStorage.setItem("token", JSON.stringify(payload));
  state.userData = null;
  state.isGetMeInitialized = state.isSystemAdmin;
  state.subjectLoggedIn = false;
  state.sponsorId = null;
  localStorage.removeItem("userData");
};

const slice = createSlice({
  name: "auth",
  initialState: {
    user: tokenUser,
    token: savedToken ? savedToken : null,
    subjectLoggedIn: persistedFlags.subjectLoggedIn,
    sponsorId: persistedFlags.sponsorId,
    isSystemAdmin: initialIsSystemAdmin,
    userData: persistedUserData,
    isGetMeLoading: false,
    isGetMeInitialized: !!persistedUserData || initialIsSystemAdmin,
  },
  reducers: {
    setSubjectLoggedIn: (state, action) => {
      state.subjectLoggedIn = true;
    },
    setGetMeLoading: (state, action) => {
      state.isGetMeLoading = action.payload;
    },
    setTokens: (state, action) => {
      const payload = action.payload;
      if (!payload?.access_token) return;
      applyTokenToState(state, payload);
    },
    updateAccessTokens: (state, action) => {
      const payload = action.payload;
      if (!payload?.access_token) return;
      state.token = payload;
      state.user = jwt_decode(payload.access_token);
      state.isSystemAdmin = isSystemAdminFromToken(state.user);
      localStorage.setItem("token", JSON.stringify(payload));
    },
    clearUserData: (state) => {
      state.userData = null;
      state.isGetMeInitialized = state.isSystemAdmin;
      state.subjectLoggedIn = false;
      state.sponsorId = null;
      localStorage.removeItem("userData");
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.userData = null;
      state.subjectLoggedIn = false;
      state.sponsorId = null;
      state.isSystemAdmin = false;
      state.isGetMeInitialized = false;
      state.isGetMeLoading = false;
      localStorage.removeItem("token");
      localStorage.removeItem("userData");
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(bloqcibeApi.endpoints.login.matchFulfilled, (state, { payload }) => {
        if (payload.access_token) {
          applyTokenToState(state, payload);
        }
      })
      .addMatcher(bloqcibeApi.endpoints.verifyAuthOTP.matchFulfilled, (state, { payload }) => {
        if (payload.access_token) {
          applyTokenToState(state, payload);
        }
      })
      .addMatcher(bloqcibeApi.endpoints.getMeUser.matchPending, (state) => {
        state.isGetMeLoading = true;
      })
      .addMatcher(bloqcibeApi.endpoints.getMeUser.matchFulfilled, (state, { payload }) => {
        if (payload) {
          state.userData = payload;
          state.isGetMeLoading = false;
          state.isGetMeInitialized = true;
          const flags = deriveFlagsFromUserData(payload);
          state.sponsorId = flags.sponsorId;
          state.subjectLoggedIn = flags.subjectLoggedIn;
          try {
            localStorage.setItem("userData", JSON.stringify(payload));
          } catch (error) {
            console.error("Error persisting user data:", error);
          }
        }
      })
      .addMatcher(bloqcibeApi.endpoints.getMeUser.matchRejected, (state) => {
        state.isGetMeLoading = false;
        // Avoid infinite loading when getMe fails (e.g. after session expiry).
        // Cached userData is kept for background refetch failures; auth is cleared separately.
        state.isGetMeInitialized = true;
      });
  },
});

export default slice.reducer;
export const { setSubjectLoggedIn, setGetMeLoading, clearUserData, clearAuth, setTokens, updateAccessTokens } = slice.actions;

export const selectCurrentUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectSponsorId = (state) => state.auth.userData?.sponsorId ?? state.auth.sponsorId;
export const selectSubjectLoggedIn = (state) => {
  if (state.auth.userData?.userRoles) {
    return state.auth.userData.userRoles.some((role) => role.role === "subject");
  }
  return state.auth.subjectLoggedIn;
};
export const selectIsSystemAdmin = (state) =>
  isSystemAdminFromToken(state.auth.user) || state.auth.isSystemAdmin;
export const selectIsSubject = (state) => selectSubjectLoggedIn(state);
export const selectHasAnyRole = (state) =>
  selectIsSystemAdmin(state) || (state.auth.userData?.userRoles?.length ?? 0) > 0;
export const selectSubjectContexts = (state) => state.auth.userData?.subjectContexts || [];
export const selectUserData = (state) => state.auth.userData;
export const selectUserRoles = (state) => state.auth.userData?.userRoles || [];
export const selectIsGetMeInitialized = (state) =>
  state.auth.isGetMeInitialized || selectIsSystemAdmin(state);
export const selectIsGetMeLoading = (state) => state.auth.isGetMeLoading;
